import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { verdictLabel } from '@aiinterview/shared';
import { api } from '../api/client';
import { useSession } from '../api/session';
import {
    Button, Card, StatusPill, Loading, ErrorNote, EmptyState
} from '../components/ui';
import { DataTable } from '../components/DataTable';
import { Avatar } from '../components/Avatar';

interface DashboardData {
    counts: {
        candidates: number;
        pending: number;
        inProgress: number;
        completed: number;
        awaitingReview: number;
    };
    verdicts: {
        total: number;
        bands: { verdict: string; count: number; percent: number }[];
    };
    recent: {
        id: number;
        candidateName: string;
        templateName: string;
        status: string;
        reviewStatus: string;
        integrityScore: number | null;
        integrityVerdict: string | null;
        aiOverallScore: number | null;
        createdAt: string;
    }[];
    topCandidates: {
        sessionId: number;
        candidateName: string;
        position: string | null;
        aiOverallScore: number;
        maxScore: number;
        scorePercent: number;
        integrityScore: number | null;
        integrityVerdict: string | null;
        reviewStatus: string;
    }[];
    llmConfigured: boolean;
    storageProvider: string;
}

/** The tone class each verdict band uses for its pill and its bar. */
const VERDICT_TONE: Record<string, string> = {
    Clean: 'is-completed',
    Minor: 'is-awaiting',
    Suspicious: 'is-awaiting',
    HighRisk: 'is-bad'
};

const TILES = [
    { key: 'candidates', label: 'Candidates', badge: 'is-people', icon: 'bi-people', to: '/candidates' },
    { key: 'pending', label: 'Not started', badge: 'is-waiting', icon: 'bi-hourglass', to: '/interviews?status=Pending' },
    { key: 'inProgress', label: 'In progress', badge: 'is-live', icon: 'bi-arrow-repeat', to: '/interviews?status=InProgress' },
    { key: 'completed', label: 'Completed', badge: 'is-done', icon: 'bi-check-circle', to: '/interviews?status=Completed' },
    { key: 'awaitingReview', label: 'Awaiting review', badge: 'is-review', icon: 'bi-eye', to: '/interviews?status=AwaitingReview' }
] as const;

export function Dashboard() {
    const { user } = useSession();
    const queryClient = useQueryClient();

    const { data, isLoading, error, isFetching } = useQuery({
        queryKey: ['dashboard'],
        queryFn: () => api.get<DashboardData>('/api/admin/dashboard')
    });

    if (isLoading) return <Loading />;
    if (error) return <ErrorNote message={(error as Error).message} />;
    if (!data) return null;

    const today = new Date().toLocaleDateString(undefined, {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="app-page-heading">Dashboard</h1>
                    <p className="app-page-lede">Welcome back, {user?.userName}</p>
                </div>

                <div className="flex items-center gap-2">
                    <span className="app-btn app-btn-secondary" style={{ cursor: 'default' }}>
                        <i className="bi bi-calendar3" aria-hidden="true" />
                        {today}
                    </span>
                    <Button
                        variant="secondary"
                        busy={isFetching}
                        onClick={() => queryClient.invalidateQueries({ queryKey: ['dashboard'] })}
                    >
                        <i className="bi bi-arrow-clockwise" aria-hidden="true" />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Informational, not a warning: running without an AI provider is supported, and
                proctoring is unaffected by it. */}
            {!data.llmConfigured && (
                <p className="app-card app-card-body text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                    No AI provider is configured, so interviews use the curated question bank and
                    answers are not scored. Everything else, including proctoring, works normally.
                </p>
            )}

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
                {TILES.map((tile) => (
                    <Link key={tile.key} to={tile.to} className="app-card stat-tile">
                        {/* Icon and count share a row; the label sits on its own line beneath them.
                            As three bare siblings these ran together as "1Candidates", inline spans
                            on one line with the label's margin inert. */}
                        <span className="stat-row">
                            <span className={`stat-badge ${tile.badge}`}>
                                <i className={`bi ${tile.icon}`} aria-hidden="true" />
                            </span>
                            <span className="stat-figure">{data.counts[tile.key]}</span>
                        </span>
                        <span className="stat-label">{tile.label}</span>
                    </Link>
                ))}
            </div>

            <div className="grid gap-6 xl:grid-cols-[22rem_1fr]">
                <div className="space-y-6">
                    <Card title="Integrity verdicts">
                        <p className="mb-4 text-sm">
                            <strong className="text-lg" style={{ color: 'var(--app-heading)' }}>
                                {data.verdicts.total}
                            </strong>{' '}
                            <span style={{ color: 'var(--text-muted-safe)' }}>
                                {data.verdicts.total === 1 ? 'scored interview' : 'scored interviews'}
                            </span>
                        </p>

                        {data.verdicts.total === 0 ? (
                            <EmptyState message="No interview has been scored yet." />
                        ) : (
                            <div className="space-y-3">
                                {data.verdicts.bands.map((band) => (
                                    <div key={band.verdict}>
                                        <div className="mb-1 flex items-center justify-between gap-2">
                                            <span className={`status-pill ${VERDICT_TONE[band.verdict]}`}>
                                                {verdictLabel(band.verdict)}
                                            </span>
                                            <span className="text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                                {band.count} · {band.percent}%
                                            </span>
                                        </div>
                                        <div className="app-meter">
                                            <span
                                                className={VERDICT_TONE[band.verdict]}
                                                style={{ width: `${band.percent}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card title="Strongest candidates so far">
                        {data.topCandidates.length === 0 ? (
                            <p className="text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                                Nobody to rank yet. A candidate appears here once they have finished an
                                interview and it has been scored.
                            </p>
                        ) : (
                            <>
                                <ol className="space-y-3.5">
                                    {data.topCandidates.map((candidate) => (
                                        <li key={candidate.sessionId}>
                                            <div className="flex items-center gap-2.5">
                                                <Avatar name={candidate.candidateName} />
                                                <div className="min-w-0 flex-1">
                                                    <Link
                                                        to={`/interviews/${candidate.sessionId}`}
                                                        className="block truncate text-sm font-medium hover:underline"
                                                        style={{ color: 'var(--brand-primary)' }}
                                                    >
                                                        {candidate.candidateName}
                                                    </Link>
                                                    {candidate.position && (
                                                        <span
                                                            className="block truncate text-xs"
                                                            style={{ color: 'var(--text-muted-safe)' }}
                                                        >
                                                            {candidate.position}
                                                        </span>
                                                    )}
                                                </div>
                                                <span
                                                    className="text-sm font-semibold"
                                                    style={{ color: 'var(--app-heading)' }}
                                                >
                                                    {candidate.aiOverallScore} / {candidate.maxScore}
                                                </span>
                                            </div>
                                            <div className="app-meter mt-1.5">
                                                <span
                                                    className="is-progress"
                                                    style={{ width: `${candidate.scorePercent}%` }}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                                {/* The ranking rule, stated rather than left to be inferred from the
                                    absence of somebody a recruiter expected to see. */}
                                <p className="mt-4 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                    Ranked on the interview score. Anyone the camera checks flagged as
                                    high risk, or a reviewer has rejected, is left out.
                                </p>
                            </>
                        )}
                    </Card>
                </div>

                <Card
                    title="Recent interviews"
                    action={
                        <Link
                            to="/interviews"
                            className="text-sm hover:underline"
                            style={{ color: 'var(--brand-primary)' }}
                        >
                            View all interviews
                        </Link>
                    }
                >
                    <DataTable
                        rows={data.recent}
                        rowKey={(row) => row.id}
                        empty="No interviews yet. Create one from the Interviews screen."
                        columns={[
                            {
                                header: 'Candidate',
                                cell: (row) => (
                                    <div className="flex items-center gap-2.5">
                                        <Avatar name={row.candidateName} />
                                        <Link
                                            to={`/interviews/${row.id}`}
                                            className="font-medium hover:underline"
                                            style={{ color: 'var(--brand-primary)' }}
                                        >
                                            {row.candidateName}
                                        </Link>
                                    </div>
                                )
                            },
                            { header: 'Template', cell: (row) => row.templateName },
                            {
                                header: 'Interview date',
                                cell: (row) => new Date(row.createdAt).toLocaleDateString(undefined, {
                                    day: 'numeric', month: 'short', year: 'numeric'
                                })
                            },
                            {
                                header: 'Status',
                                cell: (row) => (
                                    <StatusPill
                                        kind="status"
                                        value={row.status === 'Completed' && row.reviewStatus === 'Pending'
                                            ? 'AwaitingReview'
                                            : row.status}
                                    />
                                )
                            },
                            {
                                header: 'Integrity score',
                                // Score and verdict together: the number alone means nothing to
                                // someone who has not memorised the bands.
                                cell: (row) => row.integrityScore === null
                                    ? <span style={{ color: 'var(--app-nav-section)' }}>—</span>
                                    : (
                                        <span
                                            className={`status-pill ${VERDICT_TONE[row.integrityVerdict ?? ''] ?? 'is-pending'}`}
                                        >
                                            {row.integrityScore} · {verdictLabel(row.integrityVerdict ?? '')}
                                        </span>
                                    )
                            },
                            {
                                header: 'AI score',
                                cell: (row) => row.aiOverallScore
                                    ?? <span style={{ color: 'var(--app-nav-section)' }}>—</span>
                            },
                            {
                                header: '',
                                className: 'text-right',
                                cell: (row) => (
                                    <Link to={`/interviews/${row.id}`}>
                                        <Button variant="secondary">
                                            <i className="bi bi-eye" aria-hidden="true" />
                                            View report
                                        </Button>
                                    </Link>
                                )
                            }
                        ]}
                    />

                    {data.recent.length > 0 && (
                        <p className="mt-3 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                            Showing the {data.recent.length} most recent.
                        </p>
                    )}
                </Card>
            </div>
        </div>
    );
}
